import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";

const TAG_COLORS = {
  "Public cible": { bg: "bg-blue-100", text: "text-blue-800", border: "border-blue-300", activeBg: "bg-blue-600", activeText: "text-white" },
  "Adaptation de la formation": { bg: "bg-purple-100", text: "text-purple-800", border: "border-purple-300", activeBg: "bg-purple-600", activeText: "text-white" },
  "Déroulement": { bg: "bg-green-100", text: "text-green-800", border: "border-green-300", activeBg: "bg-green-600", activeText: "text-white" },
  "PSH": { bg: "bg-amber-100", text: "text-amber-800", border: "border-amber-300", activeBg: "bg-amber-600", activeText: "text-white" },
  "Moyens pédagogiques": { bg: "bg-rose-100", text: "text-rose-800", border: "border-rose-300", activeBg: "bg-rose-600", activeText: "text-white" },
  "Certification": { bg: "bg-indigo-100", text: "text-indigo-800", border: "border-indigo-300", activeBg: "bg-indigo-600", activeText: "text-white" },
  "Financement": { bg: "bg-teal-100", text: "text-teal-800", border: "border-teal-300", activeBg: "bg-teal-600", activeText: "text-white" },
  "Post-formation": { bg: "bg-orange-100", text: "text-orange-800", border: "border-orange-300", activeBg: "bg-orange-600", activeText: "text-white" },
};

const ALL_TAGS = Object.keys(TAG_COLORS);

const faqData = [
  {
    question: "Quelle formation est adaptée à mon profil et à mes objectifs ?",
    answer: (
      <>
        <p className="mb-4">Chaque parcours est conçu pour répondre à des besoins spécifiques, en fonction de votre expérience, vos ambitions et votre disponibilité. Voici comment choisir la formation qui vous correspond :</p>

        <div className="mb-4">
          <p className="font-semibold mb-1">Pour les formateurs expérimentés (NDA ou Qualiopi déjà obtenus)</p>
          <p><strong>Objectif :</strong> Officialiser votre expertise sans perdre de temps.</p>
          <p><strong>Solution :</strong> L'accompagnement VAE (Validation des Acquis de l'Expérience)</p>
          <p>Obtenez la certification FPA en valorisant uniquement votre expérience, sans heures de cours ni examen.</p>
          <p>Un dossier personnalisé, analysé par nos experts, met en lumière votre pratique et votre savoir-faire.</p>
          <p><strong>Idéal pour :</strong> Gagner en légitimité et en visibilité sans contrainte de formation supplémentaire.</p>
        </div>

        <div className="mb-4">
          <p className="font-semibold mb-1">Pour les formateurs en activité souhaitant se perfectionner</p>
          <p><strong>Objectif :</strong> Capitaliser sur la période estivale pour booster votre offre.</p>
          <p><strong>Solution :</strong> L'Université d'été (juin-juillet)</p>
          <p>Profitez de la baisse saisonnière d'activité pour suivre un programme intensif et certifiant.</p>
          <p><strong>Résultat :</strong> Une nouvelle certification à intégrer dans vos prestations dès la rentrée de septembre.</p>
          <p><strong>Idéal pour :</strong> Développer votre activité avec des outils concrets et un réseau élargi.</p>
        </div>

        <div className="mb-4">
          <p className="font-semibold mb-1">Pour les experts techniques manquant d'aisance relationnelle</p>
          <p><strong>Objectif :</strong> Renforcer votre impact pédagogique et votre confiance en animation.</p>
          <p><strong>Solution :</strong> Le Pack Expert</p>
          <p>Développez vos compétences en éloquence, prise de parole et gestion de groupe.</p>
          <p>Bénéficiez d'un double avantage : Un accompagnement VAE et des ateliers pratiques pour dynamiser vos formations.</p>
          <p><strong>Idéal pour :</strong> Transformer votre expertise technique en une expérience d'apprentissage engageante pour vos participants.</p>
        </div>

        <div className="mb-4">
          <p className="font-semibold mb-1">Pour les pédagogues en reconversion</p>
          <p><strong>Objectif :</strong> Structurer votre transition vers le métier de formateur.</p>
          <p><strong>Solution :</strong> La session longue (début en septembre ou janvier)</p>
          <p>Un parcours complet sur plusieurs mois, avec un suivi individualisé pour maîtriser tous les aspects du métier.</p>
          <p><strong>Inclus :</strong> Modules théoriques, mises en situation et accompagnement à la création de votre offre.</p>
          <p><strong>Idéal pour :</strong> Acquérir une base solide et démarrer sereinement votre nouvelle carrière.</p>
        </div>
      </>
    ),
    tags: ["Public cible"],
  },
  {
    question: "Comment la formation s'adapte-t-elle à mes besoins spécifiques ?",
    answer: (
      <>
        <p className="mb-3">Avant votre inscription, nous organisons un entretien pédagogique approfondi en visio ou par téléphone avec notre directeur. Ce temps d'échange, d'une durée de 30 à 45 minutes, a pour but de comprendre vos enjeux, vos attentes et vos forces, mais aussi d'identifier les compétences que vous souhaitez développer en priorité.</p>
        <p className="mb-3">Concrètement, cet entretien nous permet de définir ensemble les modalités les plus adaptées à votre situation : rythme de formation, choix des modules, et même les aspects pratiques comme le financement ou la certification visée. Par exemple, si vous souhaitez renforcer votre aisance relationnelle, nous orienterons votre parcours vers des ateliers de prise de parole et des mises en situation ciblées.</p>
        <p className="mb-3">Tous les éléments clés discutés lors de cet échange sont ensuite consignés dans votre espace en ligne personnel. Cet espace sert de référence à nos formateurs, qui y puisent les informations nécessaires pour ajuster leurs conseils, leurs retours et les ressources mises à votre disposition. Ainsi, chaque interaction est pensée pour répondre à vos besoins spécifiques.</p>
        <p className="mb-3">Nous profitons également de ce moment pour répondre à toutes vos questions, qu'elles concernent la méthodologie, les débouchés ou l'organisation de votre formation. Chez nous, il n'y a pas de "mauvaises questions" : seulement des réponses claires pour vous permettre d'avancer sereinement.</p>
        <p>Enfin, votre espace en ligne devient un véritable tableau de bord où vous retrouvez vos objectifs, vos progrès et l'ensemble des ressources adaptées à votre profil. Parce qu'une formation efficace est une formation qui s'ajuste à vous, et non l'inverse.</p>
      </>
    ),
    tags: ["Adaptation de la formation"],
  },
  {
    question: "En quoi nos formations se distinguent-elles vraiment de nos concurrents ?",
    answer: (
      <p>Notre différence se fait par l'équipe pédagogique. Nos intervenants ne sont pas seulement des pédagogues : ce sont des professionnels en activité, qui connaissent les réalités du terrain (gestion de groupe, enjeux de la formation pour adultes, etc.). Des formations pratico-pratiques et des conseils concrets, actualisés et immédiatement applicables dans votre quotidien.</p>
    ),
    tags: ["Public cible"],
  },
  {
    question: "Comment se déroule concrètement le suivi de la formation ?",
    answer: (
      <>
        <p className="mb-3">Les formations sont à la journée et sauf dérogation ont lieu de 9h00 à 12h30 puis de 13h30 à 16h00.</p>
        <p className="mb-3">Toutes nos formations se font à distance, via une plateforme dédiée, simple d'utilisation et sécurisée. Avant le début de votre session, vous recevrez une convocation détaillée par email, incluant :</p>
        <ul className="list-disc list-inside mb-3 space-y-1 ml-4">
          <li>Un lien d'accès à la visioconférence (compatibles avec tous les appareils : ordinateur, tablette ou smartphone).</li>
          <li>Vos identifiants personnels pour accéder aux supports pédagogiques (PDF, vidéos, exercices).</li>
          <li>Un guide pratique pour vous connecter en quelques clics, même si vous n'êtes pas familier avec les outils numériques.</li>
        </ul>
        <p className="mb-3">Pendant la formation, notre plateforme vous permettra d'interagir avec le formateur et les autres participants et d'accéder au contenu pédagogique.</p>
        <p>Après chaque séance, les éléments suivants restent à votre disposition : les enregistrements des cours, que vous pourrez consulter et télécharger à tout moment pour réviser à votre rythme et les supports de formation, mis à jour et organisés dans votre espace personnel.</p>
      </>
    ),
    tags: ["Déroulement"],
  },
  {
    question: "Les formations sont-elles accessibles aux personnes en situation de handicap ?",
    answer: (
      <>
        <p className="mb-3">Nous mettons tout en œuvre pour adapter nos formations à vos besoins spécifiques, qu'ils soient liés à un handicap moteur, sensoriel, cognitif ou psychique.</p>
        <p className="mb-3 font-semibold">Une plateforme conçue pour s'adapter à vous</p>
        <p className="mb-3">Notre environnement numérique intègre des outils dédiés pour personnaliser votre expérience :</p>
        <ul className="list-disc list-inside mb-3 space-y-1 ml-4">
          <li>Widget d'accessibilité : ajustement des contrastes des couleurs, la taille et le type de police (dont des polices adaptées aux troubles dys), ou activez un mode de lecture simplifié.</li>
          <li>Transcriptions textuelles : Tous nos enregistrements vidéo sont accompagnés de retranscriptions, et des comptes-rendus écrits sont systématiquement proposés.</li>
        </ul>
        <p>Au-delà des outils techniques, notre référent handicap vous contacte individuellement après votre entretien pédagogique pour échanger sur vos besoins, co-construire des solutions avec vous et nos formateurs (ex : adaptation des supports, aménagements des évaluations) et transmettre les informations utiles à l'équipe pédagogique, dans le respect de votre confidentialité.</p>
      </>
    ),
    tags: ["Déroulement", "PSH"],
  },
  {
    question: "Quel matériel ou logiciel dois-je avoir pour suivre la formation ?",
    answer: (
      <>
        <p className="mb-3">Pour accéder à la plateforme de visioconférence :</p>
        <ul className="list-disc list-inside mb-3 space-y-1 ml-4">
          <li>Ordinateurs avec Microsoft Windows 10 (ou supérieur), ou Linux, ou MacOS 12.0 (ou supérieur)</li>
          <li>Smartphones et tablettes en version actualisée telles que Android ou iOS sont également généralement supportées</li>
          <li>Navigateur en version actualisée tel que Google Chrome 98 (ou supérieur), ou Mozilla Firefox 98 (ou supérieur), ou Safari 15.0 (ou supérieur). Internet Explorer / Edge n'est pas officiellement supporté par la Plateforme, même si en général les Services fonctionnent sur ces navigateurs.</li>
          <li>Connexion internet d'un débit de 2Mbit/s au minimum (connexion ADSL ou fibre recommandée).</li>
          <li>Webcam (intégrée à l'ordinateur ou non)</li>
          <li>Microphone fonctionnel (attention, tous les ordinateurs n'ont pas de micro intégré) ou un micro-casque</li>
        </ul>
      </>
    ),
    tags: ["Déroulement", "Moyens pédagogiques"],
  },
  {
    question: "Que faire en cas d'absence à une séance de formation ?",
    answer: (
      <>
        <p className="mb-3">Pas de panique : nous avons prévu des solutions pour que vous ne perdiez aucun élément clé de votre formation.</p>
        <p className="mb-3">D'abord, informez votre formateur et l'équipe pédagogique de votre absence au plus tôt. Cela nous permet de vous accompagner au mieux pour le rattrapage.</p>
        <p className="mb-3">Ensuite, toutes nos séances en visioconférence sont automatiquement enregistrées et mises à votre disposition sur la plateforme pour vous permettre de rattraper en toute autonomie :</p>
        <ul className="list-disc list-inside mb-3 space-y-1 ml-4">
          <li>Le replay vidéo de la séance (disponible dès la fin du créneau, matin et après-midi).</li>
          <li>Une retranscription texte de l'enregistrement pour une révision rapide.</li>
          <li>Un compte-rendu détaillé des points clés abordés, avec les exercices et supports associés.</li>
        </ul>
      </>
    ),
    tags: ["Déroulement"],
  },
  {
    question: "Comment contacter les formateurs en cas de besoin ?",
    answer: (
      <p>Vous retrouverez sur la plateforme les pages profil de tous vos formateurs. Il y aura une brève présentation ainsi que leurs coordonnées pour prendre leur attache si nécessaire.</p>
    ),
    tags: ["Déroulement"],
  },
  {
    question: "Puis-je adapter le rythme de la formation à mes contraintes personnelles ou professionnelles ?",
    answer: (
      <>
        <p className="mb-3">Tout dépend du parcours que vous choisissez : nous faisons notre maximum pour nous adapter à votre situation, tout en garantissant la qualité de l'accompagnement pour tous.</p>
        <p className="mb-3">Pour les parcours en VAE, la flexibilité est maximale et les rendez-vous avec nos experts peuvent être reportés ou ajustés en fonction de vos disponibilités. Merci de nous prévenir au moins 48h à l'avance pour organiser ce changement. Cette souplesse nous permet de respecter à la fois vos contraintes et le temps de nos équipes.</p>
        <p>Pour les autres parcours (Université d'été, Pack Expert, Sessions Longues) : Les horaires de cours sont définis à l'avance pour assurer une dynamique de groupe et une progression cohérente. En cas d'imprévu, vous aurez toujours accès aux replays, transcriptions et comptes-rendus.</p>
      </>
    ),
    tags: ["Déroulement"],
  },
  {
    question: "La certification est-elle reconnue et valorisée ?",
    answer: (
      <>
        <p className="mb-3">Oui, notre certification est reconnue par les professionnels du secteur et alignée sur les standards du marché. Elle est enregistrée au Répertoire National des Certifications Professionnelles, ce qui garantit sa qualité et sa conformité aux exigences nationales :{" "}
          <a href="https://www.francecompetences.fr/recherche/rncp/37275/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://www.francecompetences.fr/recherche/rncp/37275/</a>
        </p>
        <p>Cette certification est valorisée par les financeurs, les employeurs et les clients : Elle atteste de vos compétences en tant que formateur professionnel, ce qui renforce votre crédibilité.</p>
      </>
    ),
    tags: ["Certification"],
  },
  {
    question: "Quels débouchés ou métiers sont accessibles après cette certification ?",
    answer: (
      <>
        <p className="mb-3">Cette certification ouvre des portes vers plusieurs métiers, selon votre projet :</p>
        <ul className="list-disc list-inside space-y-1 ml-4">
          <li><strong>Formateur indépendant :</strong> Créez votre activité et intervenez pour des organismes de formation, des entreprises ou des institutions.</li>
          <li><strong>Formateur salarié :</strong> Intégrez un centre de formation, une entreprise (service formation interne) ou un organisme public.</li>
          <li><strong>Consultant en pédagogie :</strong> Accompagnez les entreprises dans la conception de leurs programmes de formation.</li>
          <li><strong>Responsable formation :</strong> Pilotez les dispositifs de formation au sein d'une organisation.</li>
        </ul>
      </>
    ),
    tags: ["Certification"],
  },
  {
    question: "Y a-t-il des stages à réaliser ? Les stages sont-ils obligatoires ?",
    answer: (
      <p>Hors VAE, le stage est obligatoire pour valider votre certification. Sa durée dépend du parcours choisi et sera défini pendant votre entretien pédagogique avec votre référent. Ce stage doit être réalisé dans un contexte professionnel (entreprise, organisme de formation, association...). Ce stage peut prendre plusieurs formes : animation de sessions de formation (en présentiel ou à distance), conception de supports pédagogiques pour un organisme ou une entreprise, observation et co-animation avec un formateur expérimenté...</p>
    ),
    tags: ["Déroulement"],
  },
  {
    question: "Puis-je obtenir et cumuler plusieurs aides au financement (CPF, région, Pôle Emploi...) ?",
    answer: (
      <>
        <p className="mb-3">Oui. Un panorama des différents financements possibles ainsi qu'une simulation seront réalisés pendant l'entretien pédagogique.</p>
        <p className="mb-3">Il est souvent possible de cumuler plusieurs dispositifs de financement pour réduire votre reste à charge. Voici les combinaisons les plus courantes :</p>
        <ul className="list-disc list-inside space-y-1 ml-4">
          <li><strong>CPF + OPCO :</strong> Utilisez votre CPF et complétez avec une prise en charge de votre OPCO.</li>
          <li><strong>CPF + Aide régionale :</strong> Certaines régions proposent des compléments.</li>
          <li><strong>Pôle Emploi + CPF :</strong> Si vous êtes demandeur d'emploi, Pôle Emploi peut cofinancer la formation via l'AIF (Aide Individuelle à la Formation).</li>
        </ul>
      </>
    ),
    tags: ["Financement"],
  },
  {
    question: "La formation est-elle éligible au CPF ?",
    answer: (
      <>
        <p className="mb-3">Oui, notre formation est éligible au CPF (Compte Personnel de Formation), mais les modalités de mobilisation dépendent de votre situation professionnelle et du type de droits dont vous disposez (en euros ou en heures). Voici comment procéder selon votre cas :</p>

        <div className="mb-4">
          <p className="font-semibold mb-1">Si vous disposez d'un CPF en euros (salariés du privé, indépendants, demandeurs d'emploi)</p>
          <p>Rendez-vous sur Mon Compte Formation. Dans l'onglet "Vos droits de formation", vérifiez le montant disponible en euros. Choisissez votre parcours : Lors de votre entretien pédagogique avec notre référent, nous définirons ensemble le parcours le plus adapté à vos objectifs et à votre budget. Sélectionnez le lien d'inscription correspondant à votre formation sur la plateforme Mon Compte Formation. Validez votre inscription en utilisant vos droits CPF.</p>
        </div>

        <div className="mb-4">
          <p className="font-semibold mb-1">Si vous disposez d'un CPF en heures (agents publics, certains salariés)</p>
          <p>Rendez-vous sur Mon Compte Formation. Dans l'onglet "Vos droits de formation", vérifiez le montant disponible en euros. Choisissez votre parcours : Lors de votre entretien pédagogique avec notre référent, nous définirons ensemble le parcours le plus adapté à vos objectifs et à votre budget. Obtenez un devis : Ce devis vous sera transmis par nous, en mentionnant le nombre d'heures et la prise en charge possible. Transmettez le devis à votre employeur public : Votre employeur étudiera votre demande et, en cas d'acceptation, mobilisera vos heures CPF via une convention de formation. Le règlement sera effectué directement par votre employeur après la formation.</p>
        </div>

        <div>
          <p className="font-semibold mb-1">Cas particulier pour les contrats de droit public</p>
          <p>Si vous êtes sous contrat de droit public, vous ne pouvez pas utiliser votre CPF en euros. Vous devez obligatoirement passer par la mobilisation de vos heures CPF.</p>
        </div>
      </>
    ),
    tags: ["Financement"],
  },
  {
    question: "La formation est-elle éligible selon mon OPCO ?",
    answer: (
      <>
        <p className="mb-3">Notre formation est éligible aux financements par les Opérateurs de Compétences (OPCO) et les Fonds d'Assurance Formation (FAF), sous réserve d'accord et de votre situation professionnelle (salarié, indépendant, demandeur d'emploi).</p>
        <ul className="list-disc list-inside space-y-1 ml-4">
          <li><strong>Pour les salariés :</strong> Votre employeur ou votre OPCO peut prendre en charge tout ou partie des coûts.</li>
          <li><strong>Pour les indépendants :</strong> Certains OPCO (comme l'AFDAS, l'OPCO EP, le FIF-PL etc.) proposent des aides spécifiques.</li>
        </ul>
      </>
    ),
    tags: ["Financement"],
  },
  {
    question: "Existe-t-il des aides régionales ou sectorielles pour financer la formation ?",
    answer: (
      <p>Oui, selon votre situation et votre localisation, des aides régionales ou sectorielles peuvent être disponibles. La porte d'entrée de ces dispositifs est souvent le site internet du Conseil régional où vous habitez.</p>
    ),
    tags: ["Financement"],
  },
  {
    question: "Puis-je financer ma formation moi-même ? Puis-je étaler les paiements ?",
    answer: (
      <p>Vous pouvez financer tout ou partie de votre formation sur vos fonds personnels. Lors de l'entretien pédagogique, notre référent commercial vous proposera un échéancier de paiement adapté à la durée de votre formation, avec un étalement possible jusqu'à 6 mensualités maximum, à commencer au plus tard au début de la session.</p>
    ),
    tags: ["Financement"],
  },
  {
    question: "Que se passe-t-il en cas de modification ou d'arrêt de ma formation (annulation, suspension ou abandon) ?",
    answer: (
      <>
        <p className="mb-3">Votre situation dépend du stade de votre inscription et du mode de financement utilisé.</p>

        <div className="mb-4">
          <p className="font-semibold mb-1">Si vous changez d'avis avant le début de la formation</p>
          <p className="mb-2"><strong>Délai de rétractation :</strong> Vous disposez de 14 jours après votre inscription pour annuler sans frais ni justification.</p>
          <p className="mb-2">Pour les formations financées via le CPF en euros : Les conditions d'annulation sont détaillées via ce lien :{" "}
            <a href="https://www.moncompteformation.gouv.fr/espace-public/quelles-sont-les-conditions-dannulation-dune-formation" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">moncompteformation.gouv.fr</a>
          </p>
          <p>Pour les autres financements (OPCO, employeur, paiement personnel) : Remboursement intégral si vous annulez au moins 30 jours avant le début. Des frais administratifs (jusqu'à 20 %) peuvent être retenus pour une annulation tardive. Dans tous les cas, prévenez-nous par écrit (email ou courrier) pour officialiser votre demande.</p>
        </div>

        <div className="mb-4">
          <p className="font-semibold mb-1">Si vous souhaitez suspendre temporairement votre formation</p>
          <p className="mb-2">Nous comprenons que des imprévus (santé, contraintes professionnelles, etc.) peuvent survenir.</p>
          <p className="mb-2">Pour les parcours en VAE : Les rendez-vous peuvent être reportés sans frais (sous réserve de nous prévenir 48h à l'avance).</p>
          <p>Pour les autres formations (Université d'été, pack expert, sessions longues) : Nous étudions avec vous la possibilité de reporter votre session à une date ultérieure, en fonction des places disponibles. Vous conservez l'accès aux replays et supports pour ne pas perdre le fil.</p>
        </div>

        <div>
          <p className="font-semibold mb-1">Si vous devez arrêter définitivement votre formation</p>
          <p>Un remboursement partiel est possible, calculé au prorata des heures de formation déjà consommées. Cas particulier CPF : Si vous avez utilisé vos droits CPF en euros, les règles de Mon Compte Formation s'appliquent. Pour un abandon après le début, aucun remboursement n'est généralement possible, sauf cas de force majeure.</p>
        </div>
      </>
    ),
    tags: ["Financement"],
  },
  {
    question: "Comment le passage de la certification se passe-t-il ?",
    answer: (
      <>
        <p className="mb-3">Le référentiel d'évaluation du titre est détaillé dans ce document :{" "}
          <a href="https://www.formateurpouradulte.fr/REV2_FPA_V07_21122022.pdf" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">REV2_FPA_V07_21122022.pdf</a>
        </p>
        <p>Un temps de formation sera dédié à la préparation du passage de la certification pour expliquer la démarche, les attentes, les modalités d'échecs et de repassage et répondre à vos questions selon le type de parcours VAE ou autre.</p>
      </>
    ),
    tags: ["Certification"],
  },
  {
    question: "Avez-vous des statistiques sur la certification et l'insertion ?",
    answer: (
      <>
        <ul className="list-disc list-inside mb-3 space-y-1 ml-4">
          <li>Taux d'insertion global à 6 mois : 78%</li>
          <li>Taux d'insertion dans le métier visé à 6 mois : 68%</li>
          <li>Taux d'insertion dans le métier visé à 2 ans : 56%</li>
        </ul>
        <p className="mb-3">Source : Fiche du titre professionnel sur le site France Compétences :{" "}
          <a href="https://www.francecompetences.fr/recherche/rncp/37275/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">francecompetences.fr</a>
        </p>
        <p>Le titre Formateur Professionnel d'Adulte est le quinzième titre le plus décerné en 2024. Source : « Bilan des titres professionnels 2024 », étude d'octobre 2025 du Ministère du Travail et des Solidarités.</p>
      </>
    ),
    tags: ["Certification"],
  },
  {
    question: "Puis-je accéder aux supports de cours après avoir fini la formation ?",
    answer: (
      <>
        <p className="mb-3">Oui. Vous continuerez à avoir accès au contenu de votre formation (enregistrements, documents, supports de formations...) jusqu'à 2 ans après votre formation. Passé ce délai, ce contenu sera automatiquement supprimé de notre base de données et vous n'y aurez plus accès.</p>
        <p>Nous vous recommandons de télécharger les éléments essentiels au fur et à mesure ou à la fin de votre formation, pour les conserver sans limite de durée. Ainsi, vous pourrez y revenir même après la fermeture de votre accès en ligne.</p>
      </>
    ),
    tags: ["Post-formation"],
  },
  {
    question: "Comment puis-je continuer à me former après cette certification ?",
    answer: (
      <>
        <p className="mb-3">Nous croyons en l'autonomie et la proactivité de nos participants, tout en vous fournissant les outils et le réseau pour avancer. Après la formation, votre apprentissage pourra se poursuivre grâce à :</p>
        <ul className="list-disc list-inside space-y-1 ml-4">
          <li>Notre centre de ressources (accessible depuis la page d'accueil). Il sera bientôt enrichi de fiches pratiques, de modèles prêts à l'emploi et d'articles pour votre veille.</li>
          <li>Des modules avancés (à venir) : Ces offres seront annoncées en priorité à nos anciens apprenants via email et sur notre site.</li>
        </ul>
      </>
    ),
    tags: ["Post-formation"],
  },
  {
    question: "Quel niveau d'études faut-il avoir pour s'inscrire à la formation ?",
    answer: (
      <p>Un niveau bac ou équivalent est requis pour suivre la formation.</p>
    ),
    tags: ["Public cible"],
  },
  {
    question: "Quelles compétences en informatique bureautiques faut-il avoir pour s'inscrire à la formation ?",
    answer: (
      <p>Il vous faut être en capacité de savoir utiliser un traitement de texte et de savoir réaliser des requêtes sur un moteur de recherche Internet.</p>
    ),
    tags: ["Public cible"],
  },
  {
    question: "Existe-t-il un âge limite pour s'inscrire ? La formation est-elle adaptée aux personnes en reconversion tardive ?",
    answer: (
      <p>Il n'y a pas d'âge limite pour apprendre ! Tous les âges sont les bienvenus pour suivre nos formations et notre organisme de formation se targue d'accompagner tous nos participants au mieux de leurs besoins spécifiques.</p>
    ),
    tags: ["Public cible"],
  },
  {
    question: "Puis-je suivre la formation si je ne suis pas francophone ?",
    answer: (
      <p>Dans le cadre européen commun de référence pour les langues (CECRL), un niveau C2 en français est requis pour suivre la formation. Plus d'information sur le CECRL et les niveaux de langues via ce lien :{" "}
        <a href="https://www.service-public.gouv.fr/particuliers/vosdroits/F34739" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">service-public.gouv.fr</a>
      </p>
    ),
    tags: ["Public cible"],
  },
  {
    question: "J'ai une autre question.",
    answer: null,
    tags: ["Public cible", "Déroulement", "Certification", "Financement", "Post-formation"],
    isContactCTA: true,
  },
];

const FAQ = () => {
  const [activeTags, setActiveTags] = useState(new Set());
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleTag = (tag) => {
    setActiveTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) {
        next.delete(tag);
      } else {
        next.add(tag);
      }
      return next;
    });
  };

  const clearFilters = () => {
    setActiveTags(new Set());
  };

  const filteredFaq = activeTags.size === 0
    ? faqData
    : faqData.filter((item) => item.tags.some((tag) => activeTags.has(tag)));

  const scrollToContactForm = () => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const element = document.getElementById("contact-form");
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    } else {
      const element = document.getElementById("contact-form");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <section className="py-20 bg-white min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Questions / Réponses
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Retrouvez ici les réponses aux questions les plus fréquemment posées sur nos formations, la certification, le financement et bien plus.
          </p>
        </motion.div>

        {/* Tag Filter Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="bg-gray-50 rounded-xl p-6 mb-10 sticky top-20 z-40 shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-gray-700">Filtrer par thème :</p>
            {activeTags.size > 0 && (
              <button
                onClick={clearFilters}
                className="text-xs text-gray-500 hover:text-gray-800 underline bg-transparent border-none cursor-pointer"
              >
                Tout afficher
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {ALL_TAGS.map((tag) => {
              const colors = TAG_COLORS[tag];
              const isActive = activeTags.has(tag);
              return (
                <button
                  key={tag}
                  onClick={() => toggleTag(tag)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all duration-200 cursor-pointer ${
                    isActive
                      ? `${colors.activeBg} ${colors.activeText} border-transparent shadow-md`
                      : `${colors.bg} ${colors.text} ${colors.border} hover:shadow-sm`
                  }`}
                >
                  {tag}
                </button>
              );
            })}
          </div>
          {activeTags.size > 0 && (
            <p className="text-xs text-gray-500 mt-3">
              {filteredFaq.length} question{filteredFaq.length > 1 ? "s" : ""} affichée{filteredFaq.length > 1 ? "s" : ""}
            </p>
          )}
        </motion.div>

        {/* FAQ Items */}
        <div className="space-y-6">
          {filteredFaq.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.03 }}
              viewport={{ once: true }}
              className="bg-gray-50 rounded-xl p-6 sm:p-8 border border-gray-100 hover:border-gray-200 transition-colors"
            >
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {item.question}
              </h2>

              {item.isContactCTA ? (
                <p className="text-gray-700">
                  <button
                    onClick={scrollToContactForm}
                    className="text-blue-600 hover:text-blue-800 font-medium underline bg-transparent border-none cursor-pointer p-0 text-base"
                  >
                    Contactez-nous via notre formulaire
                  </button>
                  .
                </p>
              ) : (
                <div className="text-gray-700 leading-relaxed">
                  {item.answer}
                </div>
              )}

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-200">
                {item.tags.map((tag) => {
                  const colors = TAG_COLORS[tag];
                  return (
                    <span
                      key={tag}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}
                    >
                      {tag}
                    </span>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>

        {/* No results message */}
        {filteredFaq.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">Aucune question ne correspond aux filtres sélectionnés.</p>
            <button
              onClick={clearFilters}
              className="mt-4 text-blue-600 hover:text-blue-800 font-medium underline bg-transparent border-none cursor-pointer"
            >
              Réinitialiser les filtres
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default FAQ;
